import { isEmpty, toLower, compact, reduce, camelCase, sortBy, escapeRegExp } from "lodash"
import { camelCaseToSpace, snakeCaseToSpace } from "helpers/string"

const NAME_PATTERN = /^[A-Za-z0-9_\-./,\\\s]{3,50}$/

export const validateCustomViewName = name => {
  return name.match(NAME_PATTERN)
}

// This approach filters based on the entire search phrase, in any order
// It can also filter by an exact match using quotation marks
function buildSearchRegex(searchPhrase) {
  const searchTerms = parseSearchPhraseIntoSearchTerms(searchPhrase)

  if (searchTerms.length === 0) {
    return null
  }

  const filterRules = searchTerms
    .map(term => createFilterRulesForTerm(term))
    .filter(Boolean)

  // Match any text that contains all required search terms, in any order, case-insensitve.
  return new RegExp("^" + filterRules.join("") + ".*$", "iu")
}

function parseSearchPhraseIntoSearchTerms(searchPhrase) {
  const searchTerms = []
  const input = (searchPhrase || "").replace(/[()]/g, "")
  let currentIndex = 0

  while (currentIndex < input.length) {
    const currentCharacter = input[currentIndex]
    if (isWhitespace(currentCharacter)) {
      currentIndex++
      continue
    }

    if (isQuoteCharacter(currentCharacter)) {
      const phraseSearchTerm = extractQuotedPhrase(input, currentIndex)
      searchTerms.push(phraseSearchTerm.term)
      currentIndex = phraseSearchTerm.nextIndex
      continue
    }

    const wordSearchTerm = extractWord(input, currentIndex)
    if (wordSearchTerm.term.value) {
      searchTerms.push(wordSearchTerm.term)
      currentIndex = wordSearchTerm.nextIndex
      continue
    }
    currentIndex++
  }

  return searchTerms
}

function isWhitespace(character) {
  return /\s/.test(character)
}

function isQuoteCharacter(character) {
  return character === '"'
}

function extractQuotedPhrase(input, startIndex) {
  let endIndex = startIndex + 1

  while (endIndex < input.length && input[endIndex] !== '"') {
    endIndex++
  }

  const phraseContent = input.slice(startIndex + 1, Math.min(endIndex, input.length))
  const nextIndex = endIndex < input.length ? endIndex + 1 : input.length

  return {
    term: { type: "phrase", value: phraseContent },
    nextIndex,
  }
}

function extractWord(input, startIndex) {
  let endIndex = startIndex

  while (
    endIndex < input.length
    && !isWhitespace(input[endIndex])
    && input[endIndex] !== "\""
  ) {
    endIndex++
  }

  return {
    term: { type: "word", value: input.slice(startIndex, endIndex) },
    nextIndex: endIndex,
  }
}

function createFilterRulesForTerm(term) {
  if (term.type === "word") {
    return createWordFilterRule(term.value)
  }

  if (term.type === "phrase") {
    return createPhraseFilterRule(term.value)
  }

  return ""
}

function createWordFilterRule(word) {
  const escapedWord = escapeRegExp(word)
  // Requires the text to contain a word that starts with escapedWord
  return `(?=.*\\b${escapedWord}\\w*\\b)`
}

function createPhraseFilterRule(phrase) {
  const escapedPhrase = escapeRegExp(phrase)
  // Requires the text to contain the exact quoted phrase as written
  return `(?=.*\\b${escapedPhrase}\\b)`
}

function getLabelText(value) {
  if (value?.label) {
    return value.label
  }
  return value || ""
}

function buildOutLabel(labelParts, nextLabel) {
  return [...(Array.isArray(labelParts) ? labelParts : []), getLabelText(nextLabel)]
    .filter(Boolean)
    .join(" ")
    .trim()
}

/**
 * Determines if a metric item matches the search criteria.
 *
 * For quoted phrases: Prioritize exact label matches, then check parent path
 * For regular search: Check full path first (most comprehensive)
 */
function matchesSearch({ searchRegex, fullName, label, hasQuotedPhrase }) {
  if (!label) return false

  if (hasQuotedPhrase) {
    if (searchRegex.test(label)) return true
    if (fullName && searchRegex.test(fullName)) return true
    if (fullName && searchRegex.test(`${fullName} ${label}`)) return true
    return false
  }

  if (fullName && searchRegex.test(`${fullName} ${label}`)) return true
  if (searchRegex.test(label)) return true
  return false
}

function filterMetricTypeOptions(metricItem, searchRegex, fullName, hasQuotedPhrase) {
  const matchingTypeOptions = metricItem.items.filter(option => {
    const optionLabel = getLabelText(option)
    return matchesSearch({
      searchRegex,
      fullName,
      label: optionLabel,
      hasQuotedPhrase,
    })
  })

  if (matchingTypeOptions.length === 0) return null

  return {
    ...metricItem,
    itemsFound: matchingTypeOptions.length,
    items: matchingTypeOptions,
  }
}

function filterSingleMetric(metricItem, searchRegex, fullName, hasQuotedPhrase) {
  const metricItemLabel = getLabelText(metricItem.label)

  if (matchesSearch({
    searchRegex,
    fullName,
    label: metricItemLabel,
    hasQuotedPhrase,
  })) {
    return {
      ...metricItem,
      itemsFound: 1,
    }
  }

  return null
}

function filterItemsWithChildren(metricItem, searchText, searchRegex, previousLabels, hasQuotedPhrase, fullName) {
  const matchingItems = metricItem.items
    .map(item => {
      const childLabel = getLabelText(item)
      if (matchesSearch({
        searchRegex,
        fullName,
        label: childLabel,
        hasQuotedPhrase,
      })) {
        return {
          ...item,
          itemsFound: 1,
        }
      }

      return filterMetricListBySearch(item, searchText, {
        searchRegex,
        previousLabels: [...previousLabels, getLabelText(metricItem.label)],
      })
    })
    .filter(Boolean)

  if (matchingItems.length === 0) return null

  return {
    ...metricItem,
    items: matchingItems,
    ...createStats(matchingItems),
  }
}

function filterMetricListBySearch(metricItem, searchText, context = {}) {
  const searchRegex = context.searchRegex || buildSearchRegex(searchText)
  if (!searchRegex) return null

  const hasQuotedPhrase = typeof searchText === "string" && searchText.includes('"')
  const previousLabels = Array.isArray(context.previousLabels) ? context.previousLabels : []
  const fullName = buildOutLabel(previousLabels, metricItem.label)

  if (metricItem.metricTypeOptions && metricItem.metricTypeOptions.length) {
    return filterMetricTypeOptions(metricItem, searchRegex, fullName, hasQuotedPhrase)
  }

  if (!metricItem.items || metricItem.items.length === 0) {
    return filterSingleMetric(metricItem, searchRegex, fullName, hasQuotedPhrase)
  }

  return filterItemsWithChildren(metricItem, searchText, searchRegex, previousLabels, hasQuotedPhrase, fullName)
}

function createStats(items) {
  return items.reduce((acc, item) => ({
    itemsFound: acc.itemsFound + (item.itemsFound || 0),
    itemsSelected: acc.itemsSelected + (item.itemsSelected || 0),
    totalItems: acc.totalItems + (item.totalItems || 0),
  }), { itemsFound: 0, itemsSelected: 0, totalItems: 0 })
}

function buildMetricTypeOptionsItem(metric, currentSelector, selectedMetrics, isSelected, metricsMap) {
  const processedOptions = metric.metricTypeOptions.map(option => {
    const selector = `${currentSelector}.${option.id}`
    metricsMap[selector] = metric.modelId ? `model.${metric.modelId}.${selector}` : selector
    return {
      ...option,
      selector,
      selected: !!selectedMetrics[selector],
    }
  })

  metricsMap[currentSelector] = metric.modelId ? `model.${metric.modelId}.${currentSelector}` : currentSelector

  return {
    ...metric,
    selector: currentSelector,
    selected: isSelected,
    itemsFound: processedOptions.length,
    itemsSelected: processedOptions.filter(opt => opt.selected).length,
    totalItems: processedOptions.length,
    shouldShowItems: true,
    items: processedOptions,
  }
}

function buildMetricItem(metric, currentSelector, isSelected, metricsMap) {
  metricsMap[currentSelector] = metric.modelId ? `model.${metric.modelId}.${currentSelector}` : currentSelector
  return {
    ...metric,
    selector: currentSelector,
    selected: isSelected,
    itemsFound: 1,
    itemsSelected: isSelected ? 1 : 0,
    totalItems: 1,
    shouldShowItems: true,
  }
}

function buildMetricItemWithChildren(metric, currentSelector, isSelected, processMetricItemFn, metricsMap) {
  const sortedItems = sortBy(metric.items, [
    item => !isEmpty(item.items),
    item => toLower(item.label),
  ])

  const processedItems = sortedItems
    .map(item => processMetricItemFn(item, metricsMap, currentSelector))
    .filter(Boolean)

  const stats = createStats(processedItems)

  metricsMap[currentSelector] = metric.modelId ? `model.${metric.modelId}.${currentSelector}` : currentSelector

  return {
    ...metric,
    selector: currentSelector,
    selected: isSelected,
    items: processedItems,
    shouldShowItems: true,
    ...stats,
  }
}

export function processMetrics(availableMetrics, selectedMetricsColumns) {
  if (!availableMetrics) {
    return { processedAvailableMetrics: {}, availableMetricsMap: {} }
  }

  // Convert selected metrics array to object for faster lookups
  const selectedMetrics = compact(selectedMetricsColumns).reduce((acc, value) => {
    acc[value] = true
    return acc
  }, {})

  // Process a single metric item and its children
  function processMetricItem(metric, metricsMap, parentSelector = "") {
    const currentSelector = compact([parentSelector, metric.id]).join(".")
    const isSelected = !!selectedMetrics[currentSelector]
      || (metric.metricTypeOptions?.length === 1 && !!selectedMetrics[`${currentSelector}.${metric.metricTypeOptions[0].id}`])

    // Handle items with metric type options
    if (metric.metricTypeOptions) {
      return buildMetricTypeOptionsItem(metric, currentSelector, selectedMetrics, isSelected, metricsMap)
    }

    // Handle leaf nodes (metrics without children)
    if (!metric.items) {
      return buildMetricItem(metric, currentSelector, isSelected, metricsMap)
    }

    // Process child items
    return buildMetricItemWithChildren(metric, currentSelector, isSelected, processMetricItem, metricsMap)
  }

  // Process all available metrics
  return reduce(availableMetrics, (result, metric) => {
    const processedMetric = processMetricItem(metric, result.availableMetricsMap)
    if (!processedMetric) return result

    if (metric.contentSetId) {
      result.processedAvailableMetrics = {
        ...result.processedAvailableMetrics,
        contentSets: [...(result.processedAvailableMetrics.contentSets || []), processedMetric],
      }
    } else {
      result.processedAvailableMetrics = {
        ...result.processedAvailableMetrics,
        [camelCase(metric.id)]: processedMetric,
      }
    }

    return result
  }, { processedAvailableMetrics: {}, availableMetricsMap: {} })
}

function filterContentSets(contentSets, searchValue) {
  const filteredContentSets = contentSets
    .map(contentSet => filterMetricListBySearch(contentSet, searchValue))
    .filter(Boolean)

  return filteredContentSets.length > 0 ? filteredContentSets : null
}

export function filterProcessedMetrics(processedMetrics, searchValue) {
  if (!searchValue || isEmpty(processedMetrics)) return processedMetrics

  const result = {}

  if (processedMetrics.contentSets) {
    const filteredContentSets = filterContentSets(processedMetrics.contentSets, searchValue)
    if (filteredContentSets) {
      result.contentSets = filteredContentSets
    }
  }

  Object.keys(processedMetrics).forEach(key => {
    if (key === "contentSets") return

    const filteredMetric = filterMetricListBySearch(processedMetrics[key], searchValue)
    if (filteredMetric) {
      result[key] = filteredMetric
    }
  })

  return result
}

export function makeHiddenMetric(metric) {
  if (typeof metric !== "string") {
    return ""
  }
  const splitMetric = metric.split(".")
  const adjustedMetric = splitMetric[0] === "model"
    ? splitMetric.slice(2)
    : splitMetric

  return adjustedMetric.reduce((acc, splitValue) => {
    // If the path ends in "value", it means it's a single factor metric
    // and we don't want to display the word "value"
    if (splitValue === "value") {
      return acc
    }
    const displayValue = camelCaseToSpace(snakeCaseToSpace(splitValue))
    acc.push(displayValue)
    return acc
  }, [])
    .join(" - ")
}
